

import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CustomerLayout } from '../../components/ui/Layout';
import { useStore } from '../../context/StoreContext';
import { Search, MapPin, ShoppingBag, Filter, Instagram, MessageSquare } from 'lucide-react';
import { MenuSkeleton } from '../../components/ui/Skeleton';
import { SEO } from '../../components/ui/SEO';

const MenuPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { categories, products, isLoading, restaurant, fetchRestaurantBySlug } = useStore();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'GRID' | 'LIST'>('GRID');

  useEffect(() => {
    if (slug) fetchRestaurantBySlug(slug);
  }, [slug, fetchRestaurantBySlug]);

  const filteredProducts = useMemo(() => {
    let result = products.filter(p => p.active);
    if (activeCategory !== 'all') result = result.filter(p => p.categoryId === activeCategory);
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(p => p.name.toLowerCase().includes(lower) || (p.description && p.description.toLowerCase().includes(lower)));
    }
    return result;
  }, [products, activeCategory, searchTerm]);

  if (isLoading || !restaurant) return <CustomerLayout showCart={false}><MenuSkeleton /></CustomerLayout>;

  // ENGINE DE ESTILO DINÂMICO
  const styles = restaurant.styles;
  const designTokens = {
      '--primary': styles.primaryColor || '#FF005C',
      '--secondary': styles.secondaryColor || '#00F0FF',
      '--bg': styles.backgroundColor || '#E8E8E8',
      '--surface': styles.surfaceColor || '#FFFFFF',
      '--text': styles.textColor || '#000000',
      '--radius': styles.borderRadius === 'none' ? '0' : 
                  styles.borderRadius === 'small' ? '8px' :
                  styles.borderRadius === 'medium' ? '16px' :
                  styles.borderRadius === 'large' ? '32px' : '9999px',
      '--font-h': styles.fontHeading === 'serif' ? 'serif' : 
                  styles.fontHeading === 'mono' ? 'monospace' : 'Plus Jakarta Sans',
      '--shadow': styles.shadowIntensity === 'none' ? 'none' :
                  styles.shadowIntensity === 'bold' ? '12px 12px 0px 0px rgba(0,0,0,1)' :
                  '0 10px 30px -5px rgba(0,0,0,0.1)'
  };

  return (
    <CustomerLayout>
      {/* FIX: Property 'name' does not exist on type 'InstagramProfile'. Use 'username' instead. */}
      <SEO title={`${restaurant.username} | Cardápio Online`} description={restaurant.landing?.heroSubtitle || 'Peça online.'} />
      
      {/* INJETOR DE ESTILOS CSS VARIABLES */}
      <style>{`
        :root {
          ${Object.entries(designTokens).map(([k, v]) => `${k}: ${v};`).join('\n')}
        }
        .btn-brand { background: var(--primary); color: white; border-radius: var(--radius); }
        .card-brand { background: var(--surface); border-radius: var(--radius); border: 4px solid var(--text); box-shadow: var(--shadow); }
        .font-brand-h { font-family: var(--font-h); }
      `}</style>

      <div className="min-h-screen bg-[var(--bg)] pb-64 animate-fade-in" style={{ color: 'var(--text)' }}>
        
        <header className="pt-32 pb-20 px-6 border-b-8 border-black" style={{ backgroundColor: 'var(--primary)' }}>
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-10 md:gap-20">
            <div className="p-3 bg-white border-8 border-black rotate-[-4deg] animate-float" style={{ borderRadius: 'var(--radius)' }}>
               <img src={restaurant.logoUrl} className="w-40 h-40 object-cover" alt="Logo" style={{ borderRadius: 'calc(var(--radius) / 2)' }} />
            </div>
            <div className="text-center md:text-left space-y-6">
                {/* FIX: Property 'name' does not exist on type 'InstagramProfile'. Use 'username' instead. */}
               <h1 className="text-6xl md:text-9xl font-black tracking-tighter uppercase italic leading-[0.75] font-brand-h">
                  {restaurant.username}<span className="text-white">!</span>
               </h1>
               <div className="flex flex-wrap justify-center md:justify-start gap-4">
                  <div className="bg-white border-4 border-black px-6 py-2 font-black uppercase text-xs shadow-bold-sm flex items-center gap-2" style={{ borderRadius: 'var(--radius)' }}>
                     <MapPin className="w-4 h-4" /> {restaurant.address?.split(',')[0]}
                  </div>
               </div>
            </div>
          </div>
        </header>

        <nav className="sticky top-20 z-40 bg-[var(--surface)] border-y-8 border-black p-6">
           <div className="max-w-7xl mx-auto space-y-8">
              <div className="relative flex-1 group">
                    <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-8 h-8 opacity-30" />
                    <input 
                        className="w-full p-6 pl-20 text-xl lg:text-3xl font-black uppercase italic outline-none transition-all bg-[var(--bg)] border-4 border-black focus:bg-white"
                        style={{ borderRadius: 'var(--radius)' }}
                        placeholder="BUSCAR ITEM..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
              </div>
              
              <div className="flex gap-4 overflow-x-auto no-scrollbar pb-4 snap-x">
                 <CatButton active={activeCategory === 'all'} onClick={() => setActiveCategory('all')} label="TUDO" />
                 {categories.filter(c => c.active).map(cat => (
                    <CatButton key={cat.id} active={activeCategory === cat.id} onClick={() => setActiveCategory(cat.id)} label={cat.name} sticker={cat.sticker} />
                 ))}
              </div>
           </div>
        </nav>

        <main className="max-w-7xl mx-auto px-6 py-12 lg:py-24">
            <div className={viewMode === 'GRID' ? "grid grid-cols-1 lg:grid-cols-2 gap-12" : "space-y-12"}>
                {filteredProducts.map((p, idx) => (
                    <article 
                        key={p.id} 
                        onClick={() => navigate(`/${restaurant.slug}/product/${p.id}`)} 
                        className="card-brand p-6 gap-6 group hover:translate-x-[-8px] hover:translate-y-[-8px] transition-all cursor-pointer flex flex-col md:flex-row"
                        style={{ animationDelay: `${idx * 100}ms` }}
                    >
                        <div className="w-full md:w-56 h-56 border-8 border-black rotate-2 overflow-hidden shrink-0" style={{ borderRadius: 'var(--radius)' }}>
                            <img src={p.imageUrl} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt={p.name} />
                        </div>
                        
                        <div className="flex-1 flex flex-col justify-between space-y-4">
                            <div>
                                <h3 className="text-4xl font-black uppercase italic leading-[0.8] tracking-tighter font-brand-h">{p.name}</h3>
                                <p className="text-sm font-bold opacity-60 mt-4 leading-relaxed">{p.description}</p>
                            </div>
                            
                            <div className="flex justify-between items-center">
                                <div className="text-3xl font-black italic">R$ {p.price.toFixed(2)}</div>
                                <div className="btn-brand w-16 h-16 flex items-center justify-center border-4 border-black shadow-bold-sm active:translate-y-1">
                                    <ShoppingBag className="w-8 h-8" />
                                </div>
                            </div>
                        </div>
                    </article>
                ))}
            </div>
        </main>

        <div className="fixed bottom-32 left-0 w-full px-6 z-[100] flex justify-center pointer-events-none">
            <div className="bg-black text-white px-10 py-5 border-8 border-black shadow-bold-xl flex items-center gap-10 pointer-events-auto rotate-[-1deg] animate-float" style={{ borderRadius: 'var(--radius)' }}>
                <span className="text-[10px] font-black uppercase tracking-[0.4em]">Live Support</span>
                <div className="flex gap-8">
                    {restaurant.social?.instagram && <SocialIcon icon={Instagram} link={restaurant.social.instagram} />}
                    {restaurant.social?.whatsapp && <SocialIcon icon={MessageSquare} link={restaurant.social.whatsapp} />}
                </div>
            </div>
        </div>
      </div>
    </CustomerLayout>
  );
};

const CatButton = ({ active, onClick, label, sticker }: any) => (
  <button 
    onClick={onClick} 
    className={`px-10 py-4 border-[6px] border-black font-black italic text-xl uppercase transition-all whitespace-nowrap snap-start flex items-center gap-4 ${active ? 'bg-[var(--primary)] text-white shadow-bold' : 'bg-[var(--surface)] text-[var(--text)]'}`}
    style={{ borderRadius: 'var(--radius)' }}
  >
    {sticker && <span className="text-2xl">{sticker}</span>}
    {label}
  </button>
);

const SocialIcon = ({ icon: Icon, link }: any) => (
    <a href={link} target="_blank" className="hover:text-brand-rose transition-all transform hover:scale-125">
        <Icon className="w-7 h-7" />
    </a>
);

export default MenuPage;